import { IAccount } from '@medely/types';
import { Professional } from 'graphql/generated/graphql';
import { useConfigurationContext } from '@medely/ui-kit';
import { useQuery } from '@tanstack/react-query';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { StatsigProvider, StatsigUser, useClientAsyncInit } from '@statsig/react-bindings';
import gqlRequest from 'utils/networkRequests/gqlRequest';
import { gql } from 'graphql-request';
import { useFirebaseContext } from '@medely/ui-kit/web';
import CenteredLoader from '@src/components/CenteredLoader';

interface IContext {
  statsigUser?: StatsigUser;
  isLoadingStatsig?: boolean;
  setCustomAttributes?: (
    customAttributes: Record<string, string | number | boolean | string[] | undefined>,
  ) => void;
}

const FeatureGateContext = createContext<IContext>({
  statsigUser: undefined,
  setCustomAttributes: undefined,
});

const CurrentUserQuery = gql`
  query GetCurrentUserForStatsig {
    me {
      company_id
      email
      id
      professional {
        id
        application_status
        split_pay_phase
      }
      primary_locations {
        id
      }
    }
  }
`;

type CurrentUser = Pick<IAccount, 'company_id' | 'email' | 'id'> & {
  professional: Pick<Professional, 'id' | 'application_status' | 'split_pay_phase'>;
  primary_locations?: Array<{ id: number }>;
};

const useCurrentUserForStatsig = () => {
  const { googleUser } = useFirebaseContext();

  const { data, ...props } = useQuery<CurrentUser>(
    ['GetCurrentUserForStatsig'],
    async (): Promise<CurrentUser> => {
      const response = await gqlRequest(CurrentUserQuery);
      return response?.me;
    },
    {
      enabled: !!googleUser,
    },
  );
  return { ...props, currentUser: data, isLoading: props.isLoading && !!googleUser };
};

export const FeatureGateProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const [statsigUser, setStatsigUser] = useState<StatsigUser>();
  const [isUpdatingStatsigUser, setIsUpdatingStatsigUser] = useState(false);

  const config = useConfigurationContext();
  const { client, isLoading: isLoadingClient } = useClientAsyncInit(config.statsigSdkKey, {});

  const { isLoading: isLoadingCurrentUser, currentUser } = useCurrentUserForStatsig();

  /**
   * Function for setting custom attributes in the current statsig user.  This can be used to set custom attirbutes
   * based on current data from the page for more complicated feature gates/experiements (ex. gating based on the location_id
   * of a job the pro is currently clocking out of)
   */
  const setCustomAttributes = useCallback(
    async (customAttributes: Record<string, string | number | boolean | string[] | undefined>) => {
      if (isLoadingClient || !statsigUser) {
        return;
      }

      const user: StatsigUser = {
        ...statsigUser,
        custom: {
          ...(statsigUser?.custom ?? {}),
          ...customAttributes,
        },
      };

      await client.updateUserAsync(user, {
        priority: 'high',
      });
      setStatsigUser(user);
    },
    [isLoadingClient, statsigUser, client],
  );

  useEffect(() => {
    if (!isLoadingClient && currentUser?.id) {
      const handleUpdateStatsigUser = async () => {
        setIsUpdatingStatsigUser(true);

        const user: StatsigUser = {
          userID: `account_${currentUser.id}`,
          email: currentUser.email,
          custom: {
            client: 'pro',
            companyID: currentUser.company_id,
            isSaasPro: !!currentUser.company_id,
            applicationStatus: currentUser.professional?.application_status,
          },
          customIDs: {
            phase_id: `${currentUser?.professional?.split_pay_phase}`,
            professional_id: `${currentUser?.professional?.id}`,
            location_id: currentUser.primary_locations?.[0]?.id
              ? `${currentUser.primary_locations[0].id}`
              : undefined,
          },
        };

        await client.updateUserAsync(user, {
          priority: 'high',
        });

        setStatsigUser(user);
        setIsUpdatingStatsigUser(false);
      };

      handleUpdateStatsigUser();
    }
  }, [currentUser, isLoadingClient]);

  if (isLoadingClient || client.loadingStatus !== 'Ready') {
    return <CenteredLoader />;
  }

  return (
    <FeatureGateContext.Provider
      value={{
        statsigUser,
        setCustomAttributes,
        isLoadingStatsig: isUpdatingStatsigUser || isLoadingCurrentUser,
      }}
    >
      <StatsigProvider client={client}>{children}</StatsigProvider>
    </FeatureGateContext.Provider>
  );
};

export const useFeatureGateContext = (): IContext => useContext(FeatureGateContext);
